import axios from 'axios';
import { Message, Notification } from 'element-ui';
import { getToken, getAliasToken } from '@/utils/auth';
import { clearAll } from '@/utils/clear-user-information';
import { ERR_AXIOS_CANCEL_CODE } from '@/utils/error';
import * as Sentry from '@sentry/browser';
import { uuid } from 'vue-uuid';
// Vite ESM 强制禁止模块级循环依赖，改用动态 import 延迟加载
// Router 仅在拦截器回调中使用（运行时），此时模块早已加载完毕
let Router;
import('@/router/index.js').then((m) => { Router = m.default; });

// create an axios instance
let isErrorShowing = false;
const Request = axios.create({
  baseURL: '/workbench', // url = base url + request url
  // withCredentials: true, // send cookies when cross-domain requests
  timeout: 120000 // request timeout
});
// request interceptor
Request.interceptors.request.use(
  (config) => {
    const data = config.data;
    // tempSession真正登录之前，请求后端接口（如第一个login，多租户选择租户）时，需要传一个session复制体(因为前端存了真正的sessionId后一刷新页面就直接登录了)
    if (data && data.tempSession) {
      config.headers['sessionId'] = getAliasToken();
    } else {
      config.headers['sessionId'] = getToken();
    }
    // 除了ie请求缓存问题
    config.headers['Pragma'] = 'no-cache';
    //配合后端处理接口重复调用问题
    config.headers.traceId = uuid.v4();
    // 路由权限 - 热更新时优先从 window.app 获取，确保 menuId 始终可用
    let menuId = null;
    // 优先从 window.app 获取，HMR 时更可靠
    if (window?.app?.$route?.meta?.menuId) {
      menuId = window.app.$route.meta.menuId;
    } else if (Router?.currentRoute?.meta?.menuId) {
      menuId = Router.currentRoute.meta.menuId;
    } else if (Router?.app?._route?.meta?.menuId) {
      menuId = Router.app._route.meta.menuId;
    }

    if (menuId) {
      config.headers['permissionCode'] = menuId;
    }


    return config;
  },
  (error) => {
    // do something with request error
    // for debug
    return Promise.reject(error);
  }
);

// response interceptor
Request.interceptors.response.use(
  async (response) => {
    let res = response.data;

    // 下载文件流单独处理
    if (response.config?.responseType == 'blob') {
      // 下载文件流失败时，将接口返回文件流转换为json对象
      if (res.type === 'application/json') {
        res = await new Promise((resolve) => {
          if (res instanceof Blob) {
            const reader = new FileReader();
            reader.readAsText(res, 'utf-8');
            reader.onload = function () {
              let obj = reader.result || '';

              resolve(JSON.parse(obj) || '');
            };
          } else {
            resolve('');
          }
        });
      } else {
        return response;
      }
    }
    if (response.config?.requestType == 's3') {
      return res;
    }

    if (res.code !== 0) {
      // 请求异常
      // 出库异常处理
      if (res.code === 15666) {
        Message({
          message: res.msg || '请求异常',
          type: 'error',
          duration: 5 * 1000,
          dangerouslyUseHTMLString: true
        });

        // 返回上一页处理
        setTimeout(() => {
          Router.go(-1);
        }, 1500);

        return Promise.reject(new Error(res.msg || 'Error'));
      }
      if (res.code === 10086) {
        return res;
      }
      if (res.code === 30001) {
        return res;
      }
      if (res.code === 3) {
        return res;
      }
      if (res.code === 409) {
        return res;
      }
      if (res.code === 20001 || res.code === 1999 || res.code === 20002) {
        // 会议同时段客户参会不可以重复，状态码20001
        // 预算管理的页面编辑没有校验是否被扣为负数，状态码1999
        // 腾讯会议额度不足，请联系供应商，人工回填腾讯会议号，状态码20002
        return {
          code: res.code,
          ...res
        };
      }
      if (response.config?.requestType === 'message_notice') {
        return res;
      }
      if (res.code === 201000) {
        // 系统升级中
        Notification({
          type: 'warning',
          title: '提示',
          message: res.msg || '系统升级中，请稍等...',
          duration: 10 * 1000
        });
        if (Router.currentRoute.path !== '/login') {
          setTimeout(() => {
            clearAll();
            Router.replace('/login');
          }, 1000);
        }
      } else {
        if (res.code === 201000) {
          // 系统升级中
          Notification({
            type: 'warning',
            title: '提示',
            message: res.msg || '系统升级中，请稍等...',
            duration: 10 * 1000
          });
          if (Router.currentRoute.path !== '/login') {
            setTimeout(() => {
              clearAll();
              Router.replace('/login');
            }, 1000);
          }
        } else {
          if (!isErrorShowing) {
            isErrorShowing = true;
            Message({
              message: res.msg || '请求异常',
              type: 'error',
              duration: 5 * 1000,
              dangerouslyUseHTMLString: true,
              onClose: () => {
                isErrorShowing = false;
              }
            });
          }

          // TODO: 403权限不足
          if (res.code === 401 || res.code === 403) {
            // token过期或者非法，需要重新登录
            setTimeout(() => {
              clearAll();
              location.reload();
            }, 1000);
          } else {
            // 给sentry附加数据中添加请求信息
            Sentry.configureScope((scope) => {
              scope.setExtra('response', response);
            });
          }
        }

        return Promise.reject(new Error(res.msg || 'Error'));
      }
    } else {
      // 请求成功
      //配置了msgReplaceData属性且 code =0 且没有data的情况 将msg作为data返回
      if (response.config?.msgReplaceData && !res.data) {
        return res.msg;
      } else {
        return res.data;
      }
    }
  },
  (error) => {
    // 资料库接口终止报错
    if (error?.message === ERR_AXIOS_CANCEL_CODE) {
      return Promise.reject(error);
    }
    if (error?.response) {
      Sentry.configureScope((scope) => {
        scope.setExtra('response', error.response);
      });
    }
    if (error?.message == 'Request aborted') {
      return Promise.reject(error);
    }

    // Message({
    //   message: '请求超时,请稍后再试',
    //   type: 'error',
    //   duration: 5 * 1000
    // });

    return Promise.reject(error);
  }
);

export default Request;

// 添加重试工具函数
export function createRetryFunction(
  func,
  maxRetries = 3,
  delay = 1000,
  shouldRetry = () => true
) {
  return async function (...args) {
    let lastError = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await func.apply(this, args);
      } catch (error) {
        lastError = error;

        // 检查是否应该重试
        if (attempt === maxRetries || !shouldRetry(error, attempt)) {
          throw error;
        }

        // 指数退避延迟
        const retryDelay = delay * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }
    }

    throw lastError;
  };
}
